import { cycleOn } from "../domain/cycle";
import { cycleSuggestion, keptKey } from "../domain/cycleTraining";
import { DOW_LABELS, dayLabel, parseDay, todayStr } from "../domain/date";
import { sessionKcal } from "../domain/energy";
import { doseIndex, sessionFor, sessionKeyFor, typeName } from "../domain/plan";
import { programmeFor } from "../domain/programme";
import { sessionMinutes } from "../domain/queue";
import type { SessionKey } from "../domain/types";
import { gentleOffKey, plannedKey } from "../domain/week";
import { useKaya } from "../store/kayaStore";
import { keepMySession, setSessionOverride, setTravelDay } from "../store/toggles";
import { ActionBar } from "./ActionBar";
import { I } from "./icons";
import { NavRow, RowGroup } from "./Rows";
import { Sheet } from "./Sheet";
import { Tag } from "./Tag";

/* The travel session and the gentle one have switches of their own below, and cycle care is an
   extra that never replaces the day (Plan's swap list left it out for the same reason). */
const SWAP: SessionKey[] = ["A", "B", "C", "R"];
/* The Steady templates swap among themselves: a parent's day is never swapped to a circuit. (ADR-052) */
const SWAP_STEADY: SessionKey[] = ["SB", "GC", "CH", "MJ", "WK", "YB", "R"];

/**
 * "Change today": swap the session, go back to the plan, mark a travel day, pick the gentle
 * session on a period day. Owned by the Train pages; Today opens the same sheet.
 * Writes only through store/toggles.ts (ADR-019).
 */
export function ChangeTodaySheet({ open, onClose, ds }: { open: boolean; onClose: () => void; ds: string }) {
  const S = useKaya((s) => s.S);
  const update = useKaya((s) => s.update);
  if (!open) return null;

  const key = sessionKeyFor(S, ds);
  const planned = plannedKey(S, ds);
  const travel = !!S.travelDates[ds];
  const changed = !!S.overrides[ds] || travel;
  const isToday = ds === todayStr();
  const steady = !!S.profile.training && programmeFor(S, ds) === "steady";
  const swaps = steady ? SWAP_STEADY : SWAP;

  const pick = (k: SessionKey) =>
    update((d) => {
      // Picking what the plan already says is going back to the plan, not a swap to remember.
      if (k === planned) {
        setSessionOverride(d, ds, null);
        if (d.travelDates[ds]) setTravelDay(d, ds, false);
      } else setSessionOverride(d, ds, k);
    });

  const backToPlan = () =>
    update((d) => {
      setSessionOverride(d, ds, null);
      if (d.travelDates[ds]) setTravelDay(d, ds, false);
    });

  const setGentle = (on: boolean) =>
    update((d) => setSessionOverride(d, ds, on ? "P" : gentleOffKey(d, ds)));

  // The cycle line's own change comes first, with its reason (ADR-057): the offered session, or
  // the weekday's session back on a period day. A short version runs from the session screen.
  const cyc = cycleSuggestion(S, ds);
  const kept = cyc?.keep ? keptKey(S, ds) : null;
  const offered = cyc?.offer?.how === "swap" ? cyc.offer.key : null;
  const reason = cyc ? cyc.reason.charAt(0).toUpperCase() + cyc.reason.slice(1) : "";
  const suggested = kept ? (
    <NavRow
      title={`Keep my session · ${sessionFor(kept, S).name}`}
      subtitle={`${reason}: keeping your normal session is fine`}
      icon={I.refresh}
      onClick={() => update((d) => keepMySession(d, ds))}
    />
  ) : offered ? (
    <NavRow
      title={sessionFor(offered, S).name}
      subtitle={`${reason}: ${sessionMinutes(S, offered, doseIndex(S, offered, ds))} min`}
      icon={I.drop}
      tone="cycle"
      onClick={() => pick(offered)}
    />
  ) : null;

  const subtitle = (k: SessionKey) =>
    k === "R"
      ? steady
        ? "No session today"
        : "Keeps the streak"
      : steady
        ? `${sessionMinutes(S, k, doseIndex(S, k, ds))} min`
        : `${sessionMinutes(S, k, doseIndex(S, k, ds))} min · ~${sessionKcal(S, k)} kcal est.`;

  return (
    <Sheet
      title="Change today"
      sub={`${dayLabel(ds)} · the change is for ${isToday ? "today" : "this day"} only`}
      onClose={onClose}
      footer={<ActionBar inSheet primary={{ label: "Done", onClick: onClose }} />}
    >
      {suggested ? (
        <div className="train-sheet-rows">
          <RowGroup heading="Suggested for today">{suggested}</RowGroup>
        </div>
      ) : null}

      <div className="train-sheet-rows">
        <RowGroup heading="Swap to…">
          {swaps.map((k) => {
            const on = key === k;
            return (
              <button
                key={k}
                type="button"
                className="k-row k-row--link"
                aria-pressed={on}
                onClick={() => pick(k)}
              >
                <span className="k-row__txt">
                  <span className="k-row__title">
                    {sessionFor(k, S).name} {k === planned ? <Tag tone="planned" /> : null}
                  </span>
                  <span className="k-row__sub">{subtitle(k)}</span>
                </span>
                {on ? (
                  <span className="k-row__end" aria-hidden="true">
                    {I.tick}
                  </span>
                ) : null}
              </button>
            );
          })}
        </RowGroup>
      </div>

      <div className="train-sheet-rows">
        <RowGroup>
          {changed ? (
            <NavRow
              title="Back to the plan"
              subtitle={`${DOW_LABELS[parseDay(ds).getDay()]} is ${typeName(planned)}`}
              icon={I.refresh}
              onClick={backToPlan}
            />
          ) : null}
          <NavRow
            kind="toggle"
            title="Travel day"
            subtitle={steady ? "Swaps in the chair session" : "Swaps in the 15-minute floor session"}
            icon={I.plane}
            tone="sky"
            checked={travel && !S.overrides[ds]}
            onChange={(on) => update((d) => setTravelDay(d, ds, on))}
          />
          {cycleOn(S) ? (
            <NavRow
              kind="toggle"
              title="Gentle session (cycle)"
              subtitle="The 20-minute period session"
              icon={I.drop}
              tone="cycle"
              checked={key === "P"}
              onChange={setGentle}
            />
          ) : null}
        </RowGroup>
      </div>
    </Sheet>
  );
}
